/** @jsx jsx */
import { jsx } from '@emotion/core';
import {
  FormHelperText,
  Grid,
  Box,
  Link as MaterialLink,
  useTheme,
} from '@material-ui/core';
import { useLocation } from '@reach/router';
import { navigate, Link } from 'gatsby';
import { Fragment, useEffect, useState } from 'react';
import useContentMgmt, {
  PageTextContentType,
} from '../../../hooks/useContentMgmt';
import { FormState } from '../../../hooks/useFormState.types';
import routes from '../../../routes';
import CurrencyTextField from '../../currency-text-field';
import { CheckBoxWithPaper } from '../../pge-plus-common/CheckBoxWithPaper';
import { DropdownMenu } from '../../pge-plus-common/DropdownMenu';
import { FullDivider } from '../../pge-plus-common/FullDivider';
import { GridItem } from '../../pge-plus-common/Grid';
import PopoverCard from '../../pge-plus-common/PopoverCard';
import { SaveAndComeBackLink } from '../../pge-plus-common/SaveAndComeBackLink';
import { ProceedOrCancelButton } from '../../pge-plus-common/StepperForm';
import { Question } from '../../pge-plus-common/Typography';
import { RebateEligibility } from '../evcharger.types';
import { UpdateSectionEVRebatesMutationTypes } from '../../../hooks/pge-plus/useRebatesEligibility';
import useWrapWithLoader from '../../../hooks/useWrapWithLoading';
import { CurrentRebatesView } from '.';
import RadioButtonGroup from '../../pge-plus-common/RadioButtonGroup';
import { EligibleForRebatesModal } from './EligibleForRebatesModal';
import { ApplicationSectionStatus } from '../../../__generated__/pgeplus-types';
import { useEVChargerFormState } from '../../../providers/EVChargerFormStateProvider';

type IQDBOrNewSmartChargerProps = {
  content: PageTextContentType;
  form: FormState<RebateEligibility>;
  path: string;
  setPageTitle: React.Dispatch<React.SetStateAction<string>>;
  handleSaveAndExit: () => void;
  setCurrentView: React.Dispatch<
    React.SetStateAction<CurrentRebatesView | undefined>
  >;
  iqdb: boolean;
  evPulse?: boolean;
  smartCharger?: boolean;
  updateSectionEVRebatesEligibility: UpdateSectionEVRebatesMutationTypes;
  updateFormValues: (values: RebateEligibility) => boolean;
};
export type RebateEligibilityData = {
  higherRebate: boolean | undefined;
  chargerAmount: number | undefined;
  panelAmount: number | undefined;
};
export const IQDBOrNewSmartCharger = (props: IQDBOrNewSmartChargerProps) => {
  const theme = useTheme();
  const {
    content,
    form,
    setPageTitle,
    handleSaveAndExit,
    setCurrentView,
    iqdb,
    evPulse,
    smartCharger,
    updateSectionEVRebatesEligibility,
    updateFormValues,
  } = props;
  const { wrapWithLoader } = useWrapWithLoader();
  const { richText } = useContentMgmt();
  const { pathname: path, state } = useLocation();
  const pathname = path.replace(/\/$/, '');
  // @ts-ignore
  const backRoute = state?.pathname
    ? // @ts-ignore
      state?.pathname
    : evPulse || smartCharger
    ? routes.PGE_PLUS_EV_CHARGERS_REBATE_ELIGIBILITY_CHARGER_REBATE
    : null;
  const [isEligibileForRebates, setIsEligibileForRebates] = useState(false);
  const [rebateEligibilityData, setRebateEligibilityData] = useState<
    RebateEligibilityData
  >();
  const {
    formState: {
      selectCharger: { suppliedByCustomer },
    },
  } = useEVChargerFormState();

  useEffect(() => {
    setCurrentView(CurrentRebatesView.NEW_CHARGER);
    setPageTitle(content.get('LETS_CHECK_YOUR_REBATE') || '');
  }, []);

  const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null);

  const handleOpenPopover = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClosePopover = () => {
    setAnchorEl(null);
  };

  const next = wrapWithLoader(async () => {
    updateFormValues(form.values);
    const {
      validationError,
      isEligible,
      amount,
    } = await updateSectionEVRebatesEligibility(
      form.values,
      false,
      ApplicationSectionStatus.Completed,
    );
    if (!validationError) {
      updateFormValues({ ...form.values, amount: amount?.toString() });
      if (isEligible) {
        void navigate(
          suppliedByCustomer
            ? routes.PGE_PLUS_EV_CHARGERS_INSTALLATION
            : routes.PGE_PLUS_EV_CHARGERS_SELECT_CHARGER,
        );
      } else {
        void navigate(
          routes.PGE_PLUS_EV_CHARGERS_REBATE_ELIGIBILITY_INELIGIBLE,
        );
      }
    }
  });

  const householdSizeOptions = Array.from([...Array(10).keys()]).map(num => ({
    label: `${num + 1}`,
    value: `${num + 1}`,
  }));

  const confirmIncomeOptions = {
    value: 'true',
    label: content.get('I_CONFIRM_THIS_IS_MY_INCOME') || '',
  };

  return (
    <form
      onSubmit={wrapWithLoader(
        form.submit(async values => {
          updateFormValues(form.values);
          if (form.values.isOwnTesla === content.get('REUSABLE_NO')) {
            return void navigate(
              routes.PGE_PLUS_EV_CHARGERS_REBATE_ELIGIBILITY_NOREBATES,
            );
          }
          if (form.values.isOwnTesla === content.get('REUSABLE_YES')) {
            return void navigate(
              routes.PGE_PLUS_EV_CHARGERS_REBATE_ELIGIBILITY_CHARGER_REBATE_VIN,
              {
                state: { pathname },
              },
            );
          }
          if (
            form.values.isHaveWifi === content.get('REUSABLE_YES') &&
            form.values.isCommitToExtendWifi === content.get('REUSABLE_YES')
          ) {
            const {
              isEligible,
              validationError,
              higherRebate,
              chargerAmount,
              panelAmount,
              amount,
            } = await updateSectionEVRebatesEligibility(form.values, false);
            if (!validationError) {
              updateFormValues({ ...form.values, amount: amount?.toString() });
              if (isEligible) {
                setIsEligibileForRebates(true);
                setRebateEligibilityData({
                  higherRebate,
                  chargerAmount,
                  panelAmount,
                });

                /* void navigate(
                  routes.PGE_PLUS_EV_CHARGERS_REBATE_ELIGIBILITY_CONFIRMATION,
                  {
                    state: {
                      pathname,
                      higherRebate,
                      chargerAmount,
                      panelAmount,
                    },
                  },
                ); */
              } else {
                void navigate(
                  routes.PGE_PLUS_EV_CHARGERS_REBATE_ELIGIBILITY_INELIGIBLE,
                  {
                    state: {
                      applicationIncomplete: true,
                    },
                  },
                );
              }
            }
          }
        }),
      )}
      noValidate
    >
      {iqdb && (
        <Fragment>
          <Fragment>
            <Box
              css={{
                '& a': { textDecoration: 'none', fontWeight: 700 },
                marginBottom: theme.spacing(1),
              }}
            >
              <Question
                title={content.get('WOULD_YOU_LIKE_TO_CHECK')}
                detail={richText(content.get('REBATE_ELIGIBILITY_DETAILS'))}
              />
            </Box>
            <Box display={'flex'} justifyContent={'center'} mt={3}>
              <Grid xs={12} md={6}>
                <RadioButtonGroup
                  options={[
                    {
                      label: content.get('REUSABLE_YES') || '',
                      value: content.get('REUSABLE_YES') || '',
                    },
                    {
                      label: content.get('REUSABLE_NO') || '',
                      value: content.get('REUSABLE_NO') || '',
                    },
                  ]}
                  state={form.values.isNeedRebateInfo || ''}
                  setState={e => {
                    if (iqdb) {
                      void form.setValue('isNeedRebateInfo', e);
                    } else {
                      void form.setValue('isNeedRebateInfo', undefined);
                    }
                    if (e === content.get('REUSABLE_NO')) {
                      void form.setValue('houseHoldSize', undefined);
                      void form.setValue('annualHouseholdIncome', undefined);
                      void form.setValue('isAnnnualIncomeChecked', undefined);
                    }
                  }}
                />
              </Grid>
            </Box>
            {form.errors.isNeedRebateInfo && (
              <GridItem>
                <FormHelperText error>
                  {form.errors.isNeedRebateInfo}
                </FormHelperText>
              </GridItem>
            )}
            <FullDivider style={{ marginTop: '30px' }} />
          </Fragment>
          {Boolean(
            form.values.isNeedRebateInfo === content.get('REUSABLE_YES'),
          ) && (
            <Fragment>
              <Grid
                container
                spacing={2}
                css={{ justifyContent: 'space-between' }}
              >
                <Grid item xs={12} md={6}>
                  <DropdownMenu
                    name={content.get('HOUSEHOLD_SIZE') || ''}
                    options={householdSizeOptions}
                    state={form.values.houseHoldSize || ''}
                    setState={e => {
                      void form.setValue('houseHoldSize', e);
                    }}
                    errorMsg={(form.errors.houseHoldSize as string) || ''}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <CurrencyTextField
                    css={{
                      margin: theme.spacing(0, 0, 0, 0),
                      width: '100%',
                      '& label': {
                        whiteSpace: 'nowrap',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        '&.MuiInputLabel-shrink': {
                          overflow: 'unset',
                        },
                      },
                    }}
                    label={`${content.get('ANNUAL_HOUSEHOLD_INCOME')}` || ''}
                    name={content.get('ANNUAL_HOUSEHOLD_INCOME') || ''}
                    onChange={(e: any) => {
                      void form.setValue(
                        'annualHouseholdIncome',
                        e.target.value,
                      );
                    }}
                    value={form.values.annualHouseholdIncome || ''}
                    error={Boolean(form.errors.annualHouseholdIncome)}
                    helperText={form.errors.annualHouseholdIncome}
                  />
                </Grid>
              </Grid>
              <Grid css={{ marginTop: '24px' }}>
                <GridItem>
                  <CheckBoxWithPaper
                    option={confirmIncomeOptions}
                    state={form.values.isAnnnualIncomeChecked || ''}
                    setState={e => form.setValue('isAnnnualIncomeChecked', e)}
                    errorMsg={
                      (form.errors.isAnnnualIncomeChecked as string) || ''
                    }
                  />
                </GridItem>
              </Grid>
              <Grid css={{ marginTop: 24, marginBottom: 30 }}>
                <GridItem>
                  <MaterialLink
                    variant="body1"
                    onClick={(e: any) => {
                      handleOpenPopover(e);
                    }}
                    css={{
                      fontSize: '20px',
                      fontWeight: 700,
                      borderBottom: '1px dotted',
                      cursor: 'pointer',
                      '&:hover': {
                        textDecoration: 'none',
                      },
                      [theme.breakpoints.down('xs')]: {
                        fontSize: 16,
                      },
                    }}
                  >
                    {content.get('LEARN_MORE_ABOUT_GROSS_ANNUAL_INCOME')}
                  </MaterialLink>
                  <PopoverCard
                    isOpen={Boolean(anchorEl)}
                    anchorEl={anchorEl}
                    closeTooltip={handleClosePopover}
                    content={content.get('GROSS_ANNUAL_INCOME_TOOLTIP')}
                  />
                </GridItem>
              </Grid>
              <FullDivider css={{ margin: 0 }} />
            </Fragment>
          )}
        </Fragment>
      )}
      {(!Boolean(iqdb) ||
        (Boolean(iqdb) && Boolean(form.values.isNeedRebateInfo))) && (
        <Fragment>
          <Question
            title={content.get('DO_YOU_HAVE_WIFI')}
            popoverLinkContent={content.get('REUSABLE_LEARN_MORE')}
            popoverContent={content.get('DO_YOU_HAVE_WIFI_TOOLTIP')}
          />
          <Box display={'flex'} justifyContent={'center'} mt={3}>
            <Grid xs={12} md={6}>
              <RadioButtonGroup
                options={[
                  {
                    label: content.get('REUSABLE_YES') || '',
                    value: content.get('REUSABLE_YES') || '',
                  },
                  {
                    label: content.get('REUSABLE_NO') || '',
                    value: content.get('REUSABLE_NO') || '',
                  },
                ]}
                state={form.values.isHaveWifi || ''}
                setState={e => {
                  void form.setValue('isHaveWifi', e);
                  if (e === content.get('REUSABLE_YES')) {
                    void form.setValue('isOwnTesla', undefined);
                  }
                  if (e === content.get('REUSABLE_NO')) {
                    void form.setValue('isCommitToExtendWifi', undefined);
                  }
                }}
              />
            </Grid>
          </Box>
          {form.errors.isHaveWifi && (
            <GridItem>
              <FormHelperText error>{form.errors.isHaveWifi}</FormHelperText>
            </GridItem>
          )}
          <FullDivider style={{ marginTop: '30px' }} />
        </Fragment>
      )}
      {form.values.isHaveWifi === content.get('REUSABLE_YES') && (
        <Fragment>
          <Question
            title={content.get('ARE_YOU_WILLING_TO_COMMIT')}
            popoverLinkContent={content.get('REUSABLE_LEARN_MORE')}
            popoverContent={content.get('ARE_YOU_WILLING_TO_COMMIT_TOOLTIP')}
          />
          <Box display={'flex'} justifyContent={'center'} mt={3}>
            <Grid xs={12} md={6}>
              <RadioButtonGroup
                options={[
                  {
                    label: content.get('REUSABLE_YES') || '',
                    value: content.get('REUSABLE_YES') || '',
                  },
                  {
                    label: content.get('REUSABLE_NO') || '',
                    value: content.get('REUSABLE_NO') || '',
                  },
                ]}
                state={form.values.isCommitToExtendWifi || ''}
                setState={e => {
                  void form.setValue('isCommitToExtendWifi', e);
                  if (e === content.get('REUSABLE_YES')) {
                    void form.setValue('isOwnTesla', undefined);
                  }
                }}
              />
            </Grid>
          </Box>
          {form.errors.isCommitToExtendWifi && (
            <GridItem>
              <FormHelperText error>
                {form.errors.isCommitToExtendWifi}
              </FormHelperText>
            </GridItem>
          )}
          <FullDivider style={{ marginTop: '30px' }} />
        </Fragment>
      )}
      {(form.values.isHaveWifi === content.get('REUSABLE_NO') ||
        form.values.isCommitToExtendWifi === content.get('REUSABLE_NO')) && (
        <Fragment>
          <Question
            title={content.get('DO_YOU_DRIVE_TESLA')}
            popoverLinkContent={content.get('REUSABLE_LEARN_MORE')}
            popoverContent={content.get('DO_YOU_DRIVE_TESLA_TOOLTIP')}
          />
          <Box display={'flex'} justifyContent={'center'} mt={3}>
            <Grid xs={12} md={6}>
              <RadioButtonGroup
                options={[
                  {
                    label: content.get('REUSABLE_YES') || '',
                    value: content.get('REUSABLE_YES') || '',
                  },
                  {
                    label: content.get('REUSABLE_NO') || '',
                    value: content.get('REUSABLE_NO') || '',
                  },
                ]}
                state={form.values.isOwnTesla || ''}
                setState={e => form.setValue('isOwnTesla', e)}
              />
            </Grid>
          </Box>
          {form.errors.isOwnTesla && (
            <GridItem>
              <FormHelperText error>{form.errors.isOwnTesla}</FormHelperText>
            </GridItem>
          )}
        </Fragment>
      )}
      <Box css={{ marginTop: '48px' }}>
        <GridItem>
          <ProceedOrCancelButton
            back={backRoute || routes.PGE_PLUS_EV_CHARGERS_CONTACT_INFO}
          />
        </GridItem>
      </Box>
      <GridItem>
        <SaveAndComeBackLink
          content={content}
          onSaveAndExit={handleSaveAndExit}
        />
      </GridItem>
      {isEligibileForRebates && rebateEligibilityData && (
        <EligibleForRebatesModal
          content={content}
          open={isEligibileForRebates}
          handleClose={() => {
            void setIsEligibileForRebates(false);
            void next();
          }}
          rebateEligibilityData={rebateEligibilityData}
        />
      )}
    </form>
  );
};
